// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { BackOrderModel, BackOrdersCollection, GetBackOrdersParameters } from "@guestsupply/App/Services/BackorderManagementService";
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { DataViewState, setDataViewLoaded, setDataViewLoading } from "@insite/client-framework/Store/Data/DataState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { Draft } from "immer";

export interface BackorderState extends DataViewState<BackOrderModel> {
    page: number;
    pageSize?: number;
    defaultPageSize?: number;
    totalCount?: number;
    totalPages?: number;
    parameter?: GetBackOrdersParameters;

    isOpen?: boolean;
    backorder?: BackOrderModel | CartLineModel;
}

const initialState: BackorderState = {
    page: 1,
    pageSize: 12,
    defaultPageSize: 12,
    parameter: {},
    
    isLoading: {},
    byId: {},
    dataViews: {},
    errorStatusCodeById: {},
};

const reducer = {
    "xBackorder/BeginLoadOrders": (draft: Draft<BackorderState>, action: { parameter: GetBackOrdersParameters }) => {
        setDataViewLoading(draft, action.parameter);
    },
    "xBackorder/CompleteLoadOrders": (
        draft: Draft<BackorderState>,
        action: { parameter: GetBackOrdersParameters; collection: BackOrdersCollection },
    ) => {
        const {
            page,
            pageSize,
            defaultPageSize,
            totalCount,
            totalPages,
        } = action.collection;
        const collection = {
            ...action.collection,
            properties: {
                pagination: JSON.stringify({
                    page,
                    pageSize,
                    defaultPageSize,
                    totalCount,
                    totalPages,
                }),
            },
        }
        setDataViewLoaded(
            draft,
            action.parameter,
            collection,
            collection => collection.backorders!,
        );
    },
    "xBackorder/UpdateParameter": (draft: Draft<BackorderState>, action: { parameter: GetBackOrdersParameters }) => {
        draft.parameter = { ...draft.parameter, ...action.parameter };

        for (const key in draft.parameter) {
            const value = (<any>draft.parameter)[key];

            // remove empty parameters
            if (value === "" || value === undefined) {
                delete (<any>draft.parameter)[key];
            }
        }

        for (const key in action.parameter) {
            // go back to page 1 if any other parameters changed
            if (
                draft.parameter.page &&
                draft.parameter.page > 1 &&
                key !== "page" &&
                key !== "pageSize"
            ) {
                draft.parameter.page = 1;
            }
        }
    },
    "xBackorder/CompleteSetIsOpen": (
        draft: Draft<BackorderState>,
        action: {
            isOpen: boolean;
            backorder?: BackOrderModel | CartLineModel;
        },
    ) => {
        draft.isOpen = action.isOpen;
        draft.backorder = action.backorder;
    },
};

const xBackorder = createTypedReducerWithImmer(initialState, reducer);

(reducers as any).xBackorder = xBackorder;

export type xBackorder = {
    xBackorder: BackorderState;
} & ApplicationState;

type BackorderReducer = {
    [key: string]: typeof xBackorder;
};

export type BackorderActions = Parameters<BackorderReducer[keyof BackorderReducer]>[1] | AnyAction;
